import type {
  AdSelectorOptionWithAsset2,
  Asset2CampaignGqlView,
  CreativeV5Type,
  Maybe,
  ScheduleV5,
} from '../../../apis/graphql';
import { AdTypeV5 } from '../../../apis/graphql';
import { defaultSupportedTimezones } from '../../../common/commonMocks/timezones';
import { brightlineTemplates } from '../../../configs';
import type { AdTag, Asset2, DateRange, Nullable } from '../../../models';
import { defaultDateRange } from '../../../utils/validation';
import { getDateTimeWithPredefinedValues } from '../../DateInput/utils';
import type { CreativesBuilderValues } from '../CreativeBuilders/types';
import { getIASCampaignLineItemIdFromIASUrl } from '../Tags/utils';
import type { AdsPageLineItem } from '../types';
import type { NielsenTagProps } from '../utils';
import { getNielsenTag } from '../utils';
import { adTypeToCreativeType } from '.';
import type { AdFormValues } from './types';

export type AdFormInitialValuesProps = {
  type: AdTypeV5;
  nielsenTagProps?: Maybe<NielsenTagProps>;
  lineItem?: Maybe<AdsPageLineItem>;
  creativeLibraryId?: Nullable<string>;
  organizationCreativeLibraryId?: Nullable<string>;
  adTagList?: AdTag[];
};

export function makeEmptyAsset<T extends Asset2>(): Partial<T> {
  return {} as Partial<T>;
}

export const AD_SELECTOR_OPTIONS_AMOUNT = 3;

export const getDefaultAdSelectorOption = (index: number): AdSelectorOptionWithAsset2 => {
  return {
    index,
    thumbnailId: '',
    thumbnail: null,
    adSelectorVideoCreativeId: '',
    caption: '',
  };
};

/*
This fix is related to the DANADS-1517 ticket. It was implemented to resolve an issue with the Ad Selector Slate creative.
The issue is connected to the migration to Asset 2.0.
Due to time constraints, there was not enough time to configure the correct creative fragments based on the asset version.
This is a temporary solution intended to unblock release activities.
*/
export const getAdSelectorOption = (option: AdSelectorOptionWithAsset2): AdSelectorOptionWithAsset2 => {
  if (!option.thumbnailId) return option;

  return {
    ...option,
    thumbnail:
      option.thumbnail ||
      ({
        id: option.thumbnailId,
      } as Asset2CampaignGqlView),
  };
};

export const getDefaultAdSelectorSlateOptionList = (
  optionList?: AdSelectorOptionWithAsset2[] | null
): AdSelectorOptionWithAsset2[] => {
  const newOptionList: AdSelectorOptionWithAsset2[] = [];

  for (let i = 0; i < AD_SELECTOR_OPTIONS_AMOUNT; i++) {
    const foundOption = optionList?.find(({ index }) => index === i + 1);

    if (foundOption) {
      newOptionList.push(getAdSelectorOption(foundOption));
    } else {
      newOptionList.push(getDefaultAdSelectorOption(i + 1));
    }
  }

  return newOptionList.sort((a, b) => (a.index > b.index ? 1 : -1));
};

export function baseCreative(
  type: CreativeV5Type,
  creativeLibraryId?: Nullable<string>
): Omit<CreativesBuilderValues, 'mode'> & {
  filesize?: number;
  importMethod: 'search' | 'upload' | 'tag';
} {
  return {
    id: '',
    name: '',
    type,
    filesize: 0,
    importMethod: 'upload',
    creativeLibraryId: creativeLibraryId || '',
  };
}

const getDefaultTemplate = (adType: AdTypeV5): string => {
  switch (adType) {
    case AdTypeV5.AdSelector:
      return brightlineTemplates.AD_SELECTOR.key;
    case AdTypeV5.MaxSelector:
      return brightlineTemplates.MAX_SELECTOR.key;
    case AdTypeV5.GatewayShop:
      return brightlineTemplates.GATEWAY_SHOP.key;
    default:
      return '';
  }
};

export const getInitialCreativeBuilderValues = (
  mode: CreativesBuilderValues['mode'] = 'selectOption',
  adType: AdTypeV5 = AdTypeV5.Video,
  brand: string = '',
  creativeLibraryId?: Nullable<string>
): CreativesBuilderValues => {
  return {
    ...baseCreative(adTypeToCreativeType(adType), creativeLibraryId),
    mode,
    type: adTypeToCreativeType(adType),
    featureList: [],
    image: { id: '' },
    logo: { id: '' },
    offerImage: { id: '' },
    poster: { id: '' },
    slate: { id: '' },
    template: getDefaultTemplate(adType),
    url: '',
    isSubmitted: false,
    video: { id: '' },
    videoAssetList: [],
    accent: null,
    altText: brand,
    destinationUrl: '',
    pushBody: '',
    pushTitle: '',
    emailBody: '',
    livingRoomCopy: '',
    emailSubject: '',
    voiceoverUrl: '',
    optionList: getDefaultAdSelectorSlateOptionList(),
    headline: '',
    randomOrder: false,
  };
};

const defaultDateRangeShaped: DateRange = {
  ...defaultDateRange,
  pacingShare:
    defaultDateRange.pacingShare && typeof defaultDateRange.pacingShare === 'number' ? defaultDateRange.pacingShare : 0,
};

export const defaultScheduleFormikValues: ScheduleV5 = {
  dateRangeList: [defaultDateRangeShaped],
  dayPartList: [],
  timezone: defaultSupportedTimezones.ET,
  deliveryInViewerTime: false,
};

export const isLineItemScheduleIsValid = (lineItemSchedule: ScheduleV5): boolean => {
  return lineItemSchedule && !!lineItemSchedule.timezone && !!lineItemSchedule.dateRangeList.length;
};

const getLICustomPacingDanteRangeForAd = (dateRangeList: DateRange[]): DateRange => {
  let minStartDate: string = dateRangeList[0].startDate;
  let maxEndDate: Maybe<string> = dateRangeList[0].endDate;
  dateRangeList.forEach((dateRange, index) => {
    if (index !== 0) {
      if (new Date(minStartDate).getTime() > new Date(dateRange.startDate).getTime()) {
        minStartDate = dateRange.startDate;
      }

      if (!dateRange.endDate || !maxEndDate) {
        maxEndDate = null;
      } else if (new Date(maxEndDate).getTime() < new Date(dateRange.endDate).getTime()) {
        maxEndDate = dateRange.endDate;
      }
    }
  });

  return {
    startDate: minStartDate,
    endDate: maxEndDate,
    pacingShare: null,
  };
};

export const getScheduleFromLineItem = (lineItemSchedule: ScheduleV5, customPacingApplied = false): ScheduleV5 => {
  const dayPartList = lineItemSchedule.dayPartList?.filter((dayPart) => dayPart.activeDayList.length) || [];

  let dateRanges: DateRange[] = [];

  if (customPacingApplied && lineItemSchedule.dateRangeList.length > 1) {
    dateRanges.push(getLICustomPacingDanteRangeForAd(lineItemSchedule.dateRangeList));
  } else {
    dateRanges = lineItemSchedule.dateRangeList;
  }

  const dateRangeList = dateRanges.map((dateRange) => ({
    ...dateRange,
    startDate: getDateTimeWithPredefinedValues(dateRange.startDate, {}),
    endDate: dateRange.endDate ? getDateTimeWithPredefinedValues(dateRange.endDate, {}) : null,
    pacingShare: null,
  }));

  return {
    ...lineItemSchedule,
    deliveryInViewerTime: !!lineItemSchedule.deliveryInViewerTime,
    dateRangeList,
    dayPartList,
  };
};

export const getInitialIASCampaignLineItemId = (adTagList: AdTag[]): string => {
  const IASTag = adTagList.find(({ isIas }) => !!isIas);
  const { campaignId: iasCampaignId, lineItemId: iasLineItemId } = getIASCampaignLineItemIdFromIASUrl(
    IASTag?.url || ''
  );

  return iasCampaignId || iasLineItemId ? `${iasCampaignId}/${iasLineItemId}` : '';
};

export const getInitialValues = ({
  type,
  nielsenTagProps = null,
  lineItem = null,
  creativeLibraryId,
  organizationCreativeLibraryId,
  adTagList = [],
}: AdFormInitialValuesProps): AdFormValues => {
  const schedule =
    lineItem?.schedule && isLineItemScheduleIsValid(lineItem?.schedule)
      ? getScheduleFromLineItem(lineItem?.schedule, lineItem?.customPacingApplied)
      : defaultScheduleFormikValues;
  const nielsenTag: Nullable<AdTag> = nielsenTagProps ? getNielsenTag(nielsenTagProps) : null;
  const formattedAdTagList = nielsenTag ? [nielsenTag, ...adTagList] : [...adTagList];
  const brandName = lineItem?.campaign?.crmLink?.name || '';

  return {
    name: '',
    type,
    schedule,
    deliveryStatus: false,
    creative: getInitialCreativeBuilderValues(
      'selectOption',
      type,
      brandName,
      creativeLibraryId ?? organizationCreativeLibraryId
    ),
    organizationCreativeLibraryId: organizationCreativeLibraryId,
    frequencyCapList: [],
    viewabilityVendorList: [],
    adTagList: formattedAdTagList,
    targetingTermValues: [],
    targetingRule: { id: '', definition: null },
    actualStart: null,
    newAdTag: {
      url: '',
    },
    newTargeting: {
      category: null,
      dimension: null,
      values: [],
    },
    status: {
      displayName: 'Off',
      key: 'OFF',
    },
    creativeChanged: false,
    countriesTarget: null,
    iasCampaignLineItemId: getInitialIASCampaignLineItemId(adTagList),
    isLastDateRangeDisabled: false,
    lineItem: {
      id: lineItem?.id ?? '',
      parentLineOmsLink: {
        id: lineItem?.parentLineOmsLink?.id ?? '',
      },
      country: lineItem?.country || '',
      countries: (lineItem?.countries ?? []).filter((c): c is string => c != null),
      displayPublisherTarget: lineItem?.displayPublisherTarget || [],
    },
    brandName,
  };
};
